import { and, condition, type FilterNode } from '@ofmos/shared/filter-ast';
import { GROUP_MAX_LIMIT } from '@ofmos/shared/record-group';
import type { FieldMetadata, RecordGroupEntry } from '../../../data/client';
import { optionById, selectOptionsOf } from '../../../fields/fieldTypeMeta';
import type { TagTone } from '../../../ui';

/**
 * The board's shape, as pure functions: what the columns are, and what each one
 * QUERIES.
 *
 * No React, no cache, no drag library - so the two things a board gets wrong most
 * often (a record that quietly vanishes, and a drop that writes the wrong value) are
 * answerable by a unit test instead of by a manual drag.
 *
 * `groupRecords` IS GONE (record-page-and-views.md D21). Grouping a single 200-row page
 * on the client was right while a board held a few hundred records and wrong the moment
 * it held thousands: the per-column counts were counts of the loaded PAGE, and the
 * board's own notice - "showing the first 200 records" - was the honest admission that
 * the numbers meant nothing. Each column is its own filtered query now, with its own
 * count and its own cursor. What survives unchanged is `buildColumns` and the drop
 * path: `moveTarget` consumes `columnOfRecord` and `columns`, and a per-column design
 * still produces both.
 */

/**
 * The column that holds records whose group field is empty. Deliberately not a
 * valid option id: a drop into it WRITES null, and the two cases must never be
 * confusable.
 */
export const NO_OPTION_COLUMN_ID = '__no_option__';

export interface BoardColumn {
  /** The option id, or NO_OPTION_COLUMN_ID. Stable across option renames (INV-7). */
  readonly id: string;
  readonly label: string;
  readonly tone: TagTone;
  /** What a drop into this column writes into the group field. */
  readonly optionId: string | null;
}

export function buildColumns(groupField: FieldMetadata): readonly BoardColumn[] {
  const optionColumns = selectOptionsOf(groupField).map<BoardColumn>((option) => ({
    id: option.id,
    label: option.label,
    tone: option.color ?? 'neutral',
    optionId: option.id,
  }));

  // The empty bucket comes last and is ALWAYS present: the options are the
  // process a team works through, and a record with no value has to be visible
  // somewhere or the board silently loses records.
  return [
    ...optionColumns,
    {
      id: NO_OPTION_COLUMN_ID,
      label: `No ${groupField.label}`,
      tone: 'neutral',
      optionId: null,
    },
  ];
}

/**
 * THE COLUMN'S STATUS DOT, or `null` when there is nothing to say in one (wave 5 round 4, B4).
 *
 * The board's header and every card in it draw the option's colour as the dot `SelectDisplay`
 * draws for the same field in a table cell, and they follow that display's own rule: an option
 * with NO colour, and the no-value bucket, get no dot rather than an invented grey circle,
 * because a circle that stands for nothing is a second encoding of nothing. Here rather than in
 * either component, so the header and the card cannot answer it differently.
 */
export function columnDot(column: BoardColumn, groupField: FieldMetadata): TagTone | null {
  if (column.optionId === null) return null;
  return optionById(groupField, column.optionId)?.color === undefined ? null : column.tone;
}

/**
 * A SELECT TOO WIDE TO BE A BOARD (group-by-aggregation.md D23), or `null`.
 *
 * The board renders a column per option PLUS the no-value bucket and reads every header
 * count out of ONE grouped read, so it asks for `optionCount + 1` groups. At exactly
 * `GROUP_MAX_LIMIT` options that is one above the cap, which the schema REFUSES rather
 * than clamps - so the board would break at the boundary its own rule was written to
 * protect. Clamping instead would return a board whose last column silently has no count,
 * which is the failure this refusal exists to prevent.
 *
 * The same rule lives on the server in `views/validate.ts`, which is what stops such a
 * board being SAVED; this is what stops one being drawn from a picker.
 */
export function boardGroupRefusal(groupField: FieldMetadata): string | null {
  const options = selectOptionsOf(groupField).length;
  if (options < GROUP_MAX_LIMIT) return null;
  return `${groupField.label} has ${options} options, and a board asks for one count per option plus the no-value bucket - which is above the cap of ${GROUP_MAX_LIMIT}. A select this wide is not a board.`;
}

/**
 * THE HEADER COUNTS, from the ONE grouped read (D23).
 *
 * The counts used to be `withTotalCount` on each column's own query - N options, N extra
 * full scans of the filter, on every render that missed the cache. One grouped statement
 * returns all N in one scan.
 *
 * The groups handed in are the SYNTHESISED ones (`emptyGroups.ts`), so an option with no
 * rows arrives here as a real group at 0 rather than as a gap: the grouped read returns
 * nothing for it and `buildColumns` renders its column regardless, deliberately, because
 * a record with no value has to be visible somewhere or the board silently loses records.
 */
export function columnCounts(groups: readonly RecordGroupEntry[]): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const group of groups) {
    const part = group.key[0];
    if (part === undefined) continue;
    if (part.kind === 'option') counts.set(part.optionId, group.count);
    else if (part.kind === 'null') counts.set(NO_OPTION_COLUMN_ID, group.count);
  }
  return counts;
}

/**
 * WHAT ONE COLUMN ASKS FOR (D21).
 *
 * `groupField eq <optionId>` for a real option, `isEmpty` for the bucket - which is why
 * the empty bucket needs a distinct column id rather than an empty option id: the two
 * are different QUESTIONS, not two spellings of one.
 *
 * ANDed with the view's own filter rather than replacing it, so a board of a filtered
 * view shows the same population the table would, split into columns. A board that
 * ignored the view's filter would be a different list wearing the same name.
 */
export function columnFilter(
  column: BoardColumn,
  groupFieldId: string,
  viewFilter: FilterNode | undefined,
): FilterNode {
  const own =
    column.optionId === null
      ? condition(groupFieldId, 'isEmpty')
      : condition(groupFieldId, 'eq', column.optionId);
  return viewFilter === undefined ? own : and(viewFilter, own);
}

/**
 * Which column every loaded record sits in, assembled from WHICH QUERY RETURNED IT.
 *
 * This replaces reading each record's value: a per-column query already answers the
 * question by construction, and re-deriving it from the value would let the two
 * disagree for the moment an optimistic write is in flight - which is exactly when a
 * drop is being decided.
 *
 * The drop path's contract is unchanged: `moveTarget` takes this map and `columns`.
 */
export function columnOfRecord(
  idsByColumn: ReadonlyMap<string, readonly string[]>,
): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const [columnId, ids] of idsByColumn) {
    for (const recordId of ids) map.set(recordId, columnId);
  }
  return map;
}
